import { useEffect, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { compactLensBoardEvidence, lensInsightAskRequest } from '~/pages/LeLens/cards/insightBoardEvidence';
import type { BoardContext } from '~/pages/LeLens/document/BlockView';
import type { Block } from '~/pages/LeLens/document/types';

import { streamReportingAiAsk } from '~/services/ReportingAiAskService';

export default function useReaskLensBlock(block: Block, context: BoardContext) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const notice = useRef<ReturnType<typeof enqueueSnackbar>>(undefined);
  const current = useRef(context);
  current.current = context;
  const controller = useRef<AbortController>(undefined);
  useEffect(() => () => controller.current?.abort(), []);
  return useMutation({
    onMutate: () => {
      notice.current = enqueueSnackbar('Re-asking over the current board…', { persist: true });
    },
    onSettled: () => closeSnackbar(notice.current),
    mutationFn: async () => {
      const { data, editor, state } = context;
      if (!data || !editor || editor.pending || typeof block.ask !== 'string' || !block.ask.trim())
        throw new Error('Wait for the board to finish loading, then re-ask.');
      const scope = JSON.stringify([data.lensVersion, state]);
      const readings = Object.entries(data.blocks).filter(
        ([id, planned]) => id !== block.id && planned.family !== 'prose' && (planned.query || planned.queries?.length),
      );
      const receipts = new Map(
        readings.map(([id, planned]) => {
          const envelopes = (planned.queries ?? [planned.query!]).map((query) => data.queries[query]);
          return [
            id,
            {
              status: envelopes.every((envelope) => envelope?.status === 'ready') ? 'ready' : 'loading',
              columns: [
                ...new Set([
                  'reading',
                  ...envelopes.flatMap((envelope) => envelope?.columns.map((column) => column.name) ?? []),
                ]),
              ],
              rows: envelopes.flatMap((envelope) => [
                ...(envelope?.rows ?? []),
                ...(envelope?.totals?.current ? [{ ...envelope.totals.current, reading: 'Total' }] : []),
              ]),
              comparison: {
            status: 'ready' as const,
                columns: [
                  'comparison',
                  ...new Set(envelopes.flatMap((envelope) => envelope?.columns.map((column) => column.name) ?? [])),
                ],
                rows: envelopes.flatMap((envelope) =>
                  Object.entries(envelope?.compare ?? {}).flatMap(([period, rows]) =>
                    rows.map((row) => ({ ...row, comparison: period })),
                  ),
                ),
              },
              source: JSON.stringify(
                envelopes.map((envelope) => ({
                  period: envelope?.period,
                  reach: envelope?.reach,
                  columns: envelope?.columns,
                  notices: envelope?.notices,
                })),
              ),
            },
          ];
        }),
      );
      if (!readings.length || [...receipts.values()].some((receipt) => receipt.status !== 'ready'))
        throw new Error('Wait for the board to finish loading, then re-ask.');
      const evidence = compactLensBoardEvidence(
        readings.map(([id, planned]) => ({ id, title: planned.name ?? id, type: planned.kind ?? 'data' })),
        receipts,
      );
      const request = lensInsightAskRequest(block.ask, evidence, {
        period: data.period.current,
        filters: context.document.filters.map((filter) => ({
          label: filter.label ?? filter.column,
          column: filter.column,
        })),
        effectiveFilters: Object.fromEntries(
          Object.entries(state.filters ?? {}).map(([column, value]) => [column, value]),
        ),
        ranges: state.ranges,
      });
      request.context.push({
        kind: 'lens_insight_state',
        text: JSON.stringify({ state, params: data.params, period: data.period }),
        applies: 'this_turn',
      });
      let answer = '';
      let invalid = false;
      controller.current?.abort();
      controller.current = new AbortController();
      await streamReportingAiAsk(
        {
          ...request,
          engine: 'mantle',
          humanInitiated: false,
          purpose: 'lens_insight',
          origin: 'lens_chat',
          lensId: data.lensId,
        },
        {
          onProgress: () => undefined,
          onDelta: (chunk) => {
            answer += chunk;
          },
          onContent: (content) => {
            if (content.type.startsWith('lens_') || content.type === 'error') invalid = true;
            if (content.type !== 'text' || content.payload?.section === 'thought-process') return;
            const text = content.payload?.content ?? content.payload?.markdown ?? content.payload?.body;
            if (typeof text === 'string' && text.trim()) answer = text;
          },
          onStreamError: () => {
            invalid = true;
          },
          onDone: () => undefined,
        },
        { signal: controller.current.signal },
      );
      if (controller.current.signal.aborted) return;
      if (invalid || !answer.trim()) throw new Error('askRichard could not complete this answer. Re-ask to try again.');
      if (
        scope !== JSON.stringify([current.current.data?.lensVersion, current.current.state]) ||
        current.current.editor?.pending
      )
        throw new Error('The board changed while answering. Re-ask over the current view.');
      editor.apply([
        { op: 'set', id: block.id, patch: { askScope: null } },
        {
          op: 'set',
          id: block.id,
          patch: {
            text: answer.trim(),
            subtitle: `Saved ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`,
            askScope: { ...state, period: data.period.preset },
          },
        },
      ]);
    },
    onError: (error: Error) => enqueueSnackbar(error.message, { variant: 'warning' }),
  });
}
